import React, {useContext, useEffect, useState} from "react";
import { Box, FormField, RadioButton, Form, Select } from "grommet";
import Modal from "../../Modal";
import { handleErrors } from "../../../utils/errors";
import { validateEmail } from "../../../lib/validation";
import Badge from "../../Badge";
import { NotificationsContext } from "../../NotificationsProvider";

const AddPaymentModal = ({
    customerUUID,
    onClose,
    title,
    companyName,
    bodyText,
    customerEmail,
    hideEmailOptions,
    isCustomerRequest=false, //differentiate whether request came from merchant or customer
    ...rest
}) => {
    const [selectedOption, setSelectedOption] = useState("existing");
    const [selectedExistingEmail, setSelectedExistingEmail] = useState(rest?.billing_address?.email || rest?.address?.email || '');
    const [newEmail, setNewEmail] = useState();
    const [newEmailInputError, setNewEmailInputError] = useState();
    const [existingEmailInputError, setExistingEmailInputError] = useState();
    const [filteredAddresses, setFilteredAddresses] = useState([]);
    const notificationCtx = useContext(NotificationsContext);
    const fetchAddresses = () => {
        fetch(`/api/customers/${customerUUID}/addresses`, {
            method: "GET",
            headers: {
                "Content-Type": "application/json",
            }
        })
            .then(handleErrors)
            .then((data) => {
                const addresses = data?.addresses.filter(user => user.email != null) || []
                setFilteredAddresses(
                    Array.from(new Set(addresses.map((a) => a.email))).map((email) => {
                        return addresses.find((a) => a.email === email);
                    })
                );
                if (addresses.length === 0) {
                    setSelectedOption('new')
                }
            })
    }

    useEffect(() => {
        if (!hideEmailOptions && customerUUID) {
            fetchAddresses();
        }
    }, []);

    const sendUpdatePaymentMethodEmail = () => {
        if (selectedOption === "new" && (!newEmail || validateEmail(newEmail))) { // if email doest validate, it returns error message, thus we check for truthy value here.
            setNewEmailInputError('Please enter a valid email address');
            return;
        }
        setNewEmailInputError(undefined);
        let email;
        let customerRequest = !!isCustomerRequest
        if (hideEmailOptions) {
            email = customerEmail;
        } else {
            email = selectedOption === "new" ? newEmail : selectedExistingEmail
        }
        const params = {
            email,
            customer_request: customerRequest
        };
        if(!email){
            setExistingEmailInputError('Please enter a valid email address');
            setNewEmailInputError('Please enter a valid email address');
            return;
        }
        fetch(`/api/customers/${customerUUID}/request-payment-method`, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
            },
            body: JSON.stringify(params),
        })
            .then(handleErrors)
            .then((data) => {
                if (!data) {
                    return;
                }
                onClose();
            })
            .then(() => notificationCtx.showNotification({message: 'Email sent!'}))
            .catch((error) => {
                onClose();
                notificationCtx.showNotification({message: "Something went wrong. Please try again later.", type: 'error'})
            }); //do something
    }

    return (
        <Modal
            ctaText="Send"
            onClose={onClose}
            title={title}
            onSecondaryCtaClicked={onClose}
            hideDeepLink={true}
            onSubmit={sendUpdatePaymentMethodEmail}
            footerProps={{pad: {top: 'xsmall', bottom: 'small'}}}
            closeModalOnSubmit={false}
            containerProps={{width: '500px'}}
        >
            <Form validate="blur">
                <Box margin={{top: 'xsmall', bottom: "small"}}>{bodyText ? bodyText : `Send ${companyName} a link to add their payment details using:`}</Box>
                {!hideEmailOptions && (
                    <Box direction="column" gap="small">
                        <Box>
                            {filteredAddresses && filteredAddresses.length > 0 && (
                                <RadioButton
                                    name="radio"
                                    checked={selectedOption === "existing"}
                                    label="An existing email address"
                                    onChange={(event) => event.target.checked && setSelectedOption('existing')}
                                />
                            )}
                            {selectedOption === "existing" && filteredAddresses && filteredAddresses.length > 0 && (
                                <Box margin={{left: 'medium', top: 'xsmall'}}>
                                    <FormField
                                        value={selectedExistingEmail}
                                        onChange={e => {
                                            setExistingEmailInputError(undefined);
                                            setSelectedExistingEmail(e.target.value);
                                        }}
                                        name="existing_email"
                                        validate={validateEmail}
                                        placeholder="e.g. ap@acme.com"
                                        component={Select}
                                        options={filteredAddresses.map(address => ({
                                            label: (
                                                <Box direction="row" align="center" gap="small" key={address?.id}>
                                                    {address.email}
                                                    {rest?.billing_address && rest?.billing_address?.email === address.email && (<Badge>DEFAULT BILLING</Badge>)}
                                                </Box>
                                            ),
                                            value: address.email
                                        }))}
                                        error={existingEmailInputError}
                                        valueLabel={(email) => (
                                            <Box direction="row" align="center" gap="small" pad="small">
                                                {email}
                                                {rest?.billing_address && rest?.billing_address?.email === email && (<Badge>DEFAULT BILLING</Badge>)}
                                            </Box>
                                        )}
                                        labelKey={"label"}
                                        valueKey={{ key: "value", reduce: true }}
                                        plain={true}
                                    />
                                </Box>
                            )}
                        </Box>
                        <Box>
                            <RadioButton
                                name="radio"
                                checked={selectedOption === "new"}
                                label="A new email address"
                                onChange={(event) => event.target.checked && setSelectedOption('new')}
                            />
                            {selectedOption === "new" && (
                                <Box margin={{left: 'medium', top: 'xsmall'}}>
                                    <FormField
                                        name="new_email"
                                        validate={validateEmail}
                                        placeholder="e.g. ap@acme.com"
                                        value={newEmail}
                                        onChange={e => setNewEmail(e.target.value)}
                                        required
                                        error={newEmailInputError}
                                    />
                                </Box>
                            )}
                        </Box>
                    </Box>
                )}
            </Form>
        </Modal>
    );
};

export default AddPaymentModal;
